import { Avatar, Space, Typography, Flex, Progress, Tag } from 'antd';
import {
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CoffeeOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  License,
  LicenseEntitlement,
  LicenseStatus,
  LicenseType,
} from '../../types';
import { licenseEntitlements } from '../../constants';
import { UserState } from '../../slices/profile';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

const formatDate = (date?: string) => {
  if (!date) return '-';
  return dayjs(date).format('DD MMM YYYY');
};

const renderCoreLicense = (
  licenses: License[],
  theme: ReturnType<typeof useTheme>
) => {
  const activeLicenses = licenses.filter(
    (l) => l.status === LicenseStatus.ACTIVE
  );
  const coreLicense = activeLicenses.find(
    (l) => l.entitlement === LicenseEntitlement.CORE
  );

  if (coreLicense) {
    const isProduction = coreLicense.type === LicenseType.PRODUCTION;
    return (
      <Flex vertical gap={4} style={{ marginTop: 4 }}>
        <Flex gap={8} align="center">
          {isProduction ? (
            <CrownOutlined style={{ fontSize: 14, color: '#faad14' }} />
          ) : (
            <CoffeeOutlined style={{ fontSize: 14, color: '#fa8c16' }} />
          )}
          <Typography.Text style={{ fontSize: 12, fontWeight: 'bold' }}>
            {isProduction ? i18n.t('production') : i18n.t('trial')}
          </Typography.Text>
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, color: theme.antd.colorPrimary }}
          >
            <Tag color="green" bordered={false}>
              Expires on <strong>{formatDate(coreLicense.endDate)}</strong>
            </Tag>
          </Typography.Text>
          {/* <Typography.Text style={{ fontSize: 12 }}>
            {` (`}
            {formatDate(coreLicense.startDate)} {' to '}
            {formatDate(coreLicense.endDate)}
            {`) `}
          </Typography.Text> */}
        </Flex>
        <Flex vertical style={{ marginTop: 4, marginLeft: 22 }}>
          <Typography.Text style={{ fontSize: 12 }}>
            Extra Templates: <strong>{coreLicense.usedTemplates || '0'}</strong>
            {' / '}
            <strong style={{ color: theme.antd.colorPrimary }}>
              {coreLicense.extraTemplates || '0'}
            </strong>
          </Typography.Text>
          <Progress
            percent={Math.round(
              ((coreLicense.usedTemplates || 0) /
                (coreLicense.extraTemplates || 1)) *
                100
            )}
            size="small"
            strokeColor={theme.antd.colorPrimary}
            status={
              (coreLicense.usedTemplates || 0) /
                (coreLicense.extraTemplates || 1) >=
              1
                ? 'exception'
                : undefined
            }
          />
        </Flex>
        <Flex vertical style={{ marginTop: 4, marginLeft: 22 }}>
          <Typography.Text style={{ fontSize: 12 }}>
            Extra Machines:{' '}
            <strong>
              {coreLicense.usedMachines || '0'}
              {' / '}
            </strong>
            <strong style={{ color: theme.antd.colorPrimary }}>
              {coreLicense.extraMachines || '0'}
            </strong>
          </Typography.Text>
          <Progress
            percent={Math.round(
              ((coreLicense.usedMachines || 0) /
                (coreLicense.extraMachines || 1)) *
                100
            )}
            size="small"
            strokeColor={theme.antd.colorPrimary}
            status={
              (coreLicense.usedMachines || 0) /
                (coreLicense.extraMachines || 1) >=
              1
                ? 'exception'
                : undefined
            }
          />
        </Flex>
      </Flex>
    );
  }

  const inactiveCoreLicenses = licenses
    .filter((l) => l.entitlement === LicenseEntitlement.CORE)
    .sort((a, b) => (a.endDate || '').localeCompare(b.endDate || ''));

  if (inactiveCoreLicenses.length > 0) {
    const lastLicense = inactiveCoreLicenses[inactiveCoreLicenses.length - 1];
    return (
      <Flex gap={8} align="center" style={{ marginTop: 4 }}>
        <CloseCircleOutlined style={{ fontSize: 12, color: '#bfbfbf' }} />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          No License
        </Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {formatDate(lastLicense.startDate)} to{' '}
          {formatDate(lastLicense.endDate)}
        </Typography.Text>
      </Flex>
    );
  }

  return (
    <Flex gap={8} align="center" style={{ marginTop: 4 }}>
      <CloseCircleOutlined style={{ fontSize: 12, color: '#bfbfbf' }} />
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        No License
      </Typography.Text>
    </Flex>
  );
};

export interface ProfileCardProps {
  user: UserState;
  licenses: License[];
}

const ProfileCard = ({ user, licenses }: ProfileCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const activeLicenses = licenses.filter(
    (l) => l.status === LicenseStatus.ACTIVE
  );
  const otherActiveLicenses = activeLicenses.filter(
    (l) => l.entitlement !== LicenseEntitlement.CORE
  );

  return (
    <div
      style={{
        padding: '16px',
        background: '#f5f5f5',
        borderRadius: '8px',
        margin: '4px',
        minWidth: '280px',
      }}
    >
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Space>
          <Avatar
            size={44}
            style={{ backgroundColor: theme.antd.colorPrimary }}
            icon={<UserOutlined />}
          />
          <Space direction="vertical" size={0}>
            <Typography.Text strong>
              {[user.firstName, user.lastName].filter(Boolean).join(' ') ||
                user.username}
            </Typography.Text>
            {user.companyName && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {user.companyName}
              </Typography.Text>
            )}

            {user.email && (
              <Typography.Text style={{ fontSize: 12 }}>
                {user.email}
              </Typography.Text>
            )}
          </Space>
        </Space>

        {renderCoreLicense(licenses, theme)}

        {otherActiveLicenses.length > 0 && (
          <Flex vertical gap={4} style={{ marginTop: 4 }}>
            {otherActiveLicenses.map((l) => {
              const entitlement = licenseEntitlements.find(
                (e) => e.id === l.entitlement
              );
              return (
                <Flex gap={8} align="center" key={l.licenseKey}>
                  <CheckCircleOutlined
                    style={{ fontSize: 12, color: '#52c41a' }}
                  />
                  <Typography.Text style={{ fontSize: 12, fontWeight: 'bold' }}>
                    {entitlement?.title || l.entitlement}
                  </Typography.Text>
                </Flex>
              );
            })}
          </Flex>
        )}
      </Space>
    </div>
  );
};

export default ProfileCard;
